import React from 'react';
import { FormikProps, withFormik, yupToFormErrors } from 'formik';
import { ValidationError } from 'yup';

import { MAX_SUBSCRIPTION_USER } from '@/constants';
import i18n from '@/settings/i18n';
import { SubscriptionRequestErrors } from '@/types/subscriptions/request';
import { isEMBA } from '@/types/subscriptions/subscription';
import { subscriptionRequestSchema } from '@/services/validators/subscriptionRequest';
import { getDuplicationEmailIndexes } from '../Presentation/Form/Field/Users/utils';
import { FormContents, Values } from '../Presentation/FormContents';

type OuterProps = {
  serviceGroupId: number;
  values: Values;
  extraErrors: SubscriptionRequestErrors;
  billingContactId: number;
  contactId: number;
  defaultBillingContactId?: number;
  defaultContactId?: number;
  onSubmit: (params: Values) => Promise<void>;
};

export const SubscriptionForm = withFormik<OuterProps, Values>({
  mapPropsToValues: ({ values }) => values,
  validate: async (values, props) => {
    let errors: any = {};
    const { serviceGroupId } = props;
    try {
      const valuesWithServiceGroupId = {
        ...values,
        ...{ serviceGroupId },
      };
      await subscriptionRequestSchema.validate(valuesWithServiceGroupId, {
        abortEarly: false,
      });
    } catch (validationError) {
      if (ValidationError.isError(validationError)) {
        errors = yupToFormErrors(validationError);
      }
    }
    const { serviceIds } = values;
    const limit =
      isEMBA(serviceGroupId) && serviceIds.length !== 0
        ? Math.floor(MAX_SUBSCRIPTION_USER / serviceIds.length)
        : MAX_SUBSCRIPTION_USER;
    if (values.users.length > limit) {
      errors.upperLimitUser = 'error';
    }

    const dupIndexes = getDuplicationEmailIndexes(values.users);
    if (dupIndexes.length !== 0) {
      errors.users = errors.users || [];
      dupIndexes.forEach((i) => {
        errors.users[i] = {
          ...errors.users[i],
          email: i18n.t('ui.error.targeted.duplicated', {
            target: i18n.t('ui.expression.email'),
          }),
        };
      });
    }

    return errors;
  },
  handleSubmit: (values, { props, setSubmitting }) => {
    const params = { ...values };
    props.onSubmit(params).catch(() => {
      setSubmitting(false);
    });
  },
})(
  // enhanced form
  ({
    serviceGroupId,
    defaultBillingContactId,
    defaultContactId,
    extraErrors,
    errors: formikErrors,
    isSubmitting,
    touched,
    values,
    setFieldValue,
    setFieldTouched,
    handleSubmit,
  }: OuterProps & FormikProps<Values>) => {
    return (
      <FormContents
        serviceGroupId={serviceGroupId}
        defaultBillingContactId={defaultBillingContactId}
        defaultContactId={defaultContactId}
        extraErrors={extraErrors}
        formikErrors={formikErrors}
        isSubmitting={isSubmitting}
        touched={touched}
        values={values}
        setFieldValue={setFieldValue}
        setFieldTouched={setFieldTouched}
        handleSubmit={handleSubmit}
      />
    );
  },
);
